import type { Feature } from "@roastery/auth";

/**
 * What disappears when a feature is withheld (EP-38, decision 5).
 *
 * <p>One table rather than forty `if`s spread over the screens, and the reason is maintenance: hiding
 * or releasing a feature has to be an edit in one file, and a reader has to be able to answer "what
 * does a pilot actually see" without opening forty. `check-release-catalogue.mjs` (US-38.6) holds it
 * to that: every withheld key has an entry, every route here exists in the generated route tree, and
 * every `data-testid` exists in the code.
 *
 * <p>Nothing here deletes anything. The routes, the screens and their files stay exactly where they
 * were; with the gate off (the `local` profile) every one of them is reachable again.
 */
export interface ReleaseEntry {
  /** `data-testid`s of sidebar entries to leave out. */
  nav?: string[];
  /** Tab addresses to leave out of a `SectionTabs` strip. */
  tabs?: string[];
  /** Route addresses that redirect when somebody arrives at them anyway. `/x/**` covers everything below. */
  routes?: string[];
  /** `data-testid`s of buttons and cards that live inside screens which otherwise stay. */
  actions?: string[];
}

export const catalogue: Partial<Record<Feature, ReleaseEntry>> = {
  // The whole section, in one entry: the sidebar door and everything behind it. A prefix rather than
  // a list of the fourteen routes, so a tab added to Integrations later is withheld without anybody
  // remembering to come back here.
  "integration.section": {
    nav: ["nav-integrations"],
    routes: ["/integrations/**"],
  },
  // Withheld on its own as well, so that releasing the section does not release the couriers with it.
  "shipping.couriers": {
    tabs: ["/integrations/shipping"],
    routes: ["/integrations/shipping"],
    // The pickup buttons on "shipped today" need no entry: they are drawn only for a courier
    // account, and without the section there are none. The manifest stays — a list of what left
    // today is worth having with hand-written shipping too.
    actions: ["ship-dialog-tab-courier", "parcel-track-now"],
  },
  "ecommerce.marketplaces_pl": {
    // The "add shop" dialog draws its buttons from what the server offers (ConnectorService.types),
    // so no entry is needed for them here; publishing a catalogue is a marketplace screen of its own.
    routes: ["/integrations/$connectorId/publish"],
  },
  "integration.ksef": {
    tabs: ["/integrations/accounting/ksef"],
    routes: ["/integrations/accounting/ksef"],
    actions: ["order-invoice-ksef"],
  },
  "purchasing.market_feed": {
    // The manual quote stays: withholding the feed is not withholding the price.
    actions: ["market-refresh", "market-feed-state"],
  },
  "pos.import": {
    actions: ["pos-import-open"],
  },
  // Nothing exists yet (EP-11 is deferred); the entry is here so the catalogue covers the list.
  "subscriptions.b2c": {},
  // EP-51: the promotions card on the plan screen — the code field, the promotional prices and the
  // history. The plan screen itself stays; the badge beside each plan's price needs no entry, because
  // it is drawn only from offers the (withheld) endpoint returned.
  "billing.promotions": {
    actions: ["plan-promotions"],
  },
  // EP-54: the checkout and its history - a tab under Settings, the three screens behind it, and the
  // "buy" button on the plan screen. A mock-up for acceptance until EP-21; the operator shows it to
  // one roastery with an early access override.
  "billing.checkout": {
    tabs: ["/settings/billing"],
    routes: ["/settings/billing/**"],
    actions: ["plan-checkout-open"],
  },
};

/** Every entry, as pairs — for the sidebar, the tab strips and the route guard to read. */
export const releaseEntries = Object.entries(catalogue) as [Feature, ReleaseEntry][];

/** Whether `pathname` is inside a route this entry withholds. */
export function coversRoute(entry: ReleaseEntry, pathname: string): boolean {
  return (entry.routes ?? []).some((route) => {
    if (route.endsWith("/**")) {
      const prefix = route.slice(0, -3);
      return pathname === prefix || pathname.startsWith(`${prefix}/`);
    }
    return pathname === route || pathname.startsWith(`${route}/`);
  });
}
